import { useState } from "react";
import Popover from "@mui/material/Popover";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import { SettingsMenuButton } from "./SettingsMenuButton";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { Box, Divider, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useTenant } from "hooks/settings";
import { useNavigateToTenantHome } from "hooks/navigation";
import { useTenantDataOptional } from "contexts/tenant";
import { Link } from "react-router";
import { useAvailableTenantMetadataContext } from "contexts/availableTenantMetadata";

export const TenantSelectorMenu = () => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const { t } = useTranslation();
  const currentTenant = useTenant();
  const currentTenantData = useTenantDataOptional();
  const navigateToTenantHome = useNavigateToTenantHome();
  const { tenantMetadataList: availableTenantMetadataList } =
    useAvailableTenantMetadataContext();

  const handleTenantSelect = (tenantName: string) => {
    if (tenantName !== currentTenant) {
      navigateToTenantHome(tenantName);
    }
    setMenuAnchor(null);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  return (
    <>
      <SettingsMenuButton
        onClick={(e) => {
          setMenuAnchor(e.currentTarget);
        }}
      >
        <CorporateFareIcon
          sx={{
            color: "white",
            fontSize: "1.25rem",
          }}
        />
      </SettingsMenuButton>

      <Popover
        open={Boolean(menuAnchor)}
        anchorEl={menuAnchor}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{ mt: 1 }}
        slotProps={{
          paper: {
            sx: {
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              boxShadow: "none",
              overflow: "hidden",
            },
          },
        }}
      >
        <Paper
          sx={{
            p: 3,
            minWidth: 280,
            maxWidth: 320,
          }}
          elevation={0}
        >
          <Box sx={{ mb: 2.5 }}>
            <Link
              to="/mission-control"
              onClick={handleMenuClose}
              style={{ textDecoration: "none" }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 1,
                  gap: 1.5,
                  cursor: "pointer",
                  borderRadius: 1,
                  p: 1,
                  mx: -1,
                  transition: "background-color 0.2s ease-in-out",
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    bgcolor: "primary.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CorporateFareIcon
                    sx={{
                      color: "white",
                      fontSize: "1.5rem",
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: "text.primary",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t("selectOrganisation")}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      display: "block",
                    }}
                  >
                    {currentTenantData.long_name ?? currentTenant}
                  </Typography>
                </Box>
              </Box>
            </Link>
          </Box>

          <Divider sx={{ mb: 2.5 }} />

          <Stack spacing={1}>
            {availableTenantMetadataList.map((tenant) => {
              const isActive = tenant.tenant_name === currentTenant;
              return (
                <Button
                  key={tenant.tenant_name}
                  onClick={() => {
                    handleTenantSelect(tenant.tenant_name);
                  }}
                  fullWidth
                  variant="text"
                  sx={{
                    justifyContent: "flex-start",
                    textAlign: "left",
                    borderRadius: 1,
                    textTransform: "none",
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? "primary.main" : "text.primary",
                    backgroundColor: isActive
                      ? "action.selected"
                      : "transparent",
                    "&:hover": {
                      backgroundColor: isActive
                        ? "action.selected"
                        : "action.hover",
                    },
                    px: 2,
                    py: 1.5,
                    position: "relative",
                  }}
                  endIcon={
                    isActive ? (
                      <CheckRoundedIcon
                        sx={{
                          color: "primary.main",
                          fontSize: "1.25rem",
                        }}
                      />
                    ) : null
                  }
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: "inherit",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {tenant.long_name}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        display: "block",
                      }}
                    >
                      {tenant.tenant_name}
                    </Typography>
                  </Box>
                </Button>
              );
            })}
          </Stack>
        </Paper>
      </Popover>
    </>
  );
};
